import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { type LanguageCode } from '@s2w/types';
import { env } from '../env';
import { detectLocale, LOCALE_STORAGE_KEY } from './detect-locale';

import enCommon from './locales/en/common.json';
import enLobby from './locales/en/lobby.json';
import enOnboarding from './locales/en/onboarding.json';
import enContest from './locales/en/contest.json';
import enErrors from './locales/en/errors.json';

export const NAMESPACES = ['common', 'lobby', 'onboarding', 'contest', 'errors'] as const;
export type Namespace = (typeof NAMESPACES)[number];

const resources = {
  en: {
    common: enCommon,
    lobby: enLobby,
    onboarding: enOnboarding,
    contest: enContest,
    errors: enErrors,
  },
} as const;

export const supportedLanguages: readonly LanguageCode[] = env.VITE_SUPPORTED_LANGUAGES;
export const fallbackLanguage: LanguageCode = supportedLanguages[0]!;

const safeStorage = (() => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
})();

const initialLanguage = detectLocale({
  supportedLanguages,
  search: typeof window !== 'undefined' ? window.location.search : '',
  storage: safeStorage,
});

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage,
  fallbackLng: fallbackLanguage,
  supportedLngs: [...supportedLanguages],
  ns: [...NAMESPACES],
  defaultNS: 'common',
  interpolation: { escapeValue: false },
  returnNull: false,
  react: { useSuspense: false },
});

i18n.on('languageChanged', (lng: string) => {
  if (!safeStorage) return;
  try {
    safeStorage.setItem(LOCALE_STORAGE_KEY, lng);
  } catch {
    // ignore quota / privacy-mode failures
  }
});

export default i18n;
